import { Injectable, inject } from '@angular/core';
import { KEYUTIL, KJUR } from 'jsrsasign';
import * as uuid from 'uuid';
import { AuthStore } from '../stores/auth.store';
import { SecureStorageService } from './secure-storage.service';

/**
 * RSA key size in bits
 */
const RSA_KEY_SIZE: number = 2048;

/**
 * Milliseconds per second for timestamp conversion
 */
const MS_PER_SECOND: number = 1000;

/**
 * Token expiration time in seconds (5 minutes)
 */
 
const TOKEN_EXPIRATION_SECONDS: number = 5 * 60;

/**
 * Storage keys for DPoP cryptographic material
 * Now stored in secure memory-only storage instead of sessionStorage
 */
const STORAGE_KEYS: {
  SIGNED_TOKEN: string;
  PUBLIC_JWK: string;
  PRIVATE_JWK: string;
} = {
  SIGNED_TOKEN: 'DpopProof',
  PUBLIC_JWK: 'dpopProofPub',
  PRIVATE_JWK: 'dpopProofPrv',
} as const;

@Injectable({
  providedIn: 'root'
})
export class DpopTokenService {
  private readonly authStore: AuthStore = inject(AuthStore);
  private readonly secureStorage: SecureStorageService = inject(SecureStorageService);


  private createKeyPair(isForced: boolean = false): void {
    if (!this.secureStorage.getItem(STORAGE_KEYS.PRIVATE_JWK) || isForced) {
      const kp: { prvKeyObj: unknown; pubKeyObj: unknown } = KEYUTIL.generateKeypair('RSA', RSA_KEY_SIZE);
      const jwkPrv: object = KEYUTIL.getJWK(kp.prvKeyObj);
      const jwkPub: object = KEYUTIL.getJWK(kp.pubKeyObj);

      // Store keys in secure memory-only storage (not accessible via XSS)
      this.secureStorage.setItem(STORAGE_KEYS.PRIVATE_JWK, JSON.stringify(jwkPrv));
      this.secureStorage.setItem(STORAGE_KEYS.PUBLIC_JWK, JSON.stringify(jwkPub));
    }
  }

  private getHeaders(): object {
    const jwkPubStr: string | null = this.secureStorage.getItem(STORAGE_KEYS.PUBLIC_JWK);
    const jwkPub: object = JSON.parse(jwkPubStr || '{}');
    const header: object = {
      'alg': 'RS256',
      'typ': 'dpop+jwt',
      'jwk': jwkPub
    };

    return header;
  }

  private getClaims(htu: string, htm: string, isAtHasRequired: boolean = true): Record<string, unknown> {
    const currentTimestamp: number = Math.floor(Date.now() / MS_PER_SECOND);

    if(htu.includes('?')) {
      htu = htu.substring(0, htu.indexOf('?'));
    }
    
    const claims: Record<string, unknown> = {
      'jti': uuid.v4(),
      'iat': currentTimestamp,
      'exp': currentTimestamp + TOKEN_EXPIRATION_SECONDS,
      'htm': htm,
      'htu': htu.trimEnd(),
    }

    if (isAtHasRequired) {
      claims.ath = this.getAtHash();
    }

    return claims;
  }

  private getAtHash(): string {

    // get the at_hash from the id_token
    const jwt: string = this.authStore.getIdToken();
    const [, jwtPayload]: string[] = jwt.split('.');
    const jwtPayloadJsonString: string = atob(jwtPayload);
    const jwtPayloadJSON: { at_hash: string } = JSON.parse(jwtPayloadJsonString);
    const atHash: string = jwtPayloadJSON.at_hash;
    return atHash;
  }

  generateAuthDpop(htu: string, htm: string): string {

    this.createKeyPair(true);

    const jwkPrvStr: string | null = this.secureStorage.getItem(STORAGE_KEYS.PRIVATE_JWK);
    const jwkPrv: object = JSON.parse(jwkPrvStr || '{}');

    // Set headers for JWT
    const header: object = this.getHeaders();

    const claims: Record<string, unknown> = this.getClaims(htu, htm, false);

    const sHeader: string = JSON.stringify(header);
    const sPayload: string = JSON.stringify(claims);
    const signedToken: string = KJUR.jws.JWS.sign('RS256', sHeader, sPayload, KEYUTIL.getKey(jwkPrv));

    this.secureStorage.setItem(STORAGE_KEYS.SIGNED_TOKEN, signedToken);

    return signedToken;
  }



  generateDpop(htu: string, htm: string): string | undefined {

    // Ensure key pair exists (generates new keys on page refresh since secure storage is memory-only)
    this.createKeyPair();

    const jwkPrvStr: string | null = this.secureStorage.getItem(STORAGE_KEYS.PRIVATE_JWK);
    const jwkPrv: object = JSON.parse(jwkPrvStr || '{}');

    // Set headers for JWT
    const header: object = this.getHeaders();

    const claims: Record<string, unknown> = this.getClaims(htu, htm, true);

    const sHeader: string = JSON.stringify(header);
    const sPayload: string = JSON.stringify(claims);
    const signedToken: string = KJUR.jws.JWS.sign('RS256', sHeader, sPayload, KEYUTIL.getKey(jwkPrv));

    return signedToken;
  }

  /**
   * Clear all stored cryptographic material (call on logout)
   */
  clearKeys(): void {
    this.secureStorage.removeItem(STORAGE_KEYS.PRIVATE_JWK);
    this.secureStorage.removeItem(STORAGE_KEYS.PUBLIC_JWK);
    this.secureStorage.removeItem(STORAGE_KEYS.SIGNED_TOKEN);
  }
}
